import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/Paginated';
import { ReferralSource } from 'models/ReferralSource';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export type ReferralSourcesIndexParams = BaseIndexQueryParams & { search?: string };

// `aliases` is the whole list every time, not a delta: the form holds the chips it was opened with,
// so what it submits IS the intended state. Sending an empty array is how the last chip is removed,
// which is why it is never omitted.
export type ReferralSourceMutationParams = {
  name: string;
  aliases: string[];
};

export const referralSourceQueryKeys = {
  base: ['referralSources'],
  index: (params: Record<string, any> = {}) => ['referralSources', 'index', params],
};

// The two surfaces differ only in which client's list they read. Undefined clientId means the
// acting client's own list (the Configuration page); a clientId means an Olio admin is looking at
// that client's list from Admin > Clients.
function basePath(clientId?: string) {
  return clientId ? `/admin/clients/${clientId}/referral_sources` : '/referral_sources';
}

export async function indexReferralSources(
  { clientId, ...params }: ReferralSourcesIndexParams & { clientId?: string },
  signal: GenericAbortSignal
) {
  return http.get<Paginated<ReferralSource>>(basePath(clientId), { params, signal }).then((response) => response.data);
}

export async function createReferralSource({
  clientId,
  ...params
}: ReferralSourceMutationParams & { clientId?: string }) {
  return http.post<ReferralSource>(basePath(clientId), params).then((response) => response.data);
}

export async function updateReferralSource({
  id,
  clientId,
  ...params
}: ReferralSourceMutationParams & { id: string; clientId?: string }) {
  return http.patch<ReferralSource>(`${basePath(clientId)}/${id}`, params).then((response) => response.data);
}

export async function destroyReferralSource({ id, clientId }: { id: string; clientId?: string }) {
  return http.delete(`${basePath(clientId)}/${id}`);
}
